import { useEffect, useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PenSquare, Star, Trash2, PlusCircle, FileText } from "lucide-react";

function TemplateEditor({ initial, onSaved, onClose, mode }) {
  const [name, setName] = useState(initial?.name || "");
  const [pitch, setPitch] = useState(initial?.pitch || "");
  const [products, setProducts] = useState(initial?.products || "");
  const [boothSize, setBoothSize] = useState(initial?.booth_size || "standard");
  const [isDefault, setIsDefault] = useState(Boolean(initial?.is_default));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const body = { name: name.trim(), pitch, products, booth_size: boothSize, is_default: isDefault };
      if (mode === "edit" && initial?.id) {
        const { data } = await api.patch(`/vendor/application-templates/${initial.id}`, body);
        toast.success("Template updated");
        onSaved(data);
      } else {
        const { data } = await api.post("/vendor/application-templates", body);
        toast.success("Template saved");
        onSaved(data);
      }
      onClose();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 mt-2" data-testid="template-editor-form">
      <div>
        <Label className="text-xs uppercase tracking-widest">Template name</Label>
        <Input
          data-testid="template-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          maxLength={80}
          placeholder="e.g. Farmers market pitch"
          className="mt-2"
        />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest">Your pitch</Label>
        <Textarea
          data-testid="template-pitch"
          value={pitch}
          onChange={(e) => setPitch(e.target.value)}
          required
          rows={4}
          placeholder="Tell the organizer about your work…"
          className="mt-2"
        />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest">What you&apos;ll sell</Label>
        <Textarea
          data-testid="template-products"
          value={products}
          onChange={(e) => setProducts(e.target.value)}
          required
          rows={3}
          placeholder="Handmade ceramics, small batch candles…"
          className="mt-2"
        />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest">Booth size</Label>
        <Input
          data-testid="template-booth"
          value={boothSize}
          onChange={(e) => setBoothSize(e.target.value)}
          placeholder="standard / large / corner"
          className="mt-2"
        />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          data-testid="template-default"
          checked={isDefault}
          onChange={(e) => setIsDefault(e.target.checked)}
          className="h-4 w-4"
        />
        <span>Set as my default — auto-selected when I open the apply dialog</span>
      </label>
      {err && (
        <div className="text-sm rounded-md p-3" data-testid="template-error"
             style={{ backgroundColor: "rgba(31,93,52,0.08)", color: "var(--terracotta)" }}>
          {err}
        </div>
      )}
      <div className="flex gap-3 pt-2">
        <Button type="submit" disabled={busy} data-testid="template-save" className="btn-primary rounded-full">
          {busy ? "Saving…" : (mode === "edit" ? "Save changes" : "Save template")}
        </Button>
        <Button type="button" variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
      </div>
    </form>
  );
}

function TemplateCard({ t, onEdit, onDelete, onMakeDefault }) {
  return (
    <div
      data-testid={`template-card-${t.id}`}
      className="card-warm rounded-sm p-6 flex flex-col gap-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-serif text-2xl truncate">{t.name}</div>
          <div className="text-xs mt-1" style={{ color: "var(--ink-soft)" }}>
            Booth: {t.booth_size || "standard"} · Updated {(t.updated_at || "").slice(0, 10)}
          </div>
        </div>
        {t.is_default && (
          <span
            data-testid={`template-default-badge-${t.id}`}
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full"
            style={{ backgroundColor: "rgba(196,139,46,0.15)", color: "#c48b2e" }}
          >
            <Star size={10} /> Default
          </span>
        )}
      </div>
      <div className="text-sm space-y-2">
        <div>
          <div className="overline" style={{ color: "var(--ink-soft)" }}>Pitch</div>
          <p className="line-clamp-3">{t.pitch}</p>
        </div>
        <div>
          <div className="overline" style={{ color: "var(--ink-soft)" }}>Products</div>
          <p className="line-clamp-2">{t.products}</p>
        </div>
      </div>
      <div className="flex gap-2 flex-wrap mt-auto">
        <Button
          size="sm"
          variant="outline"
          data-testid={`template-edit-${t.id}`}
          onClick={() => onEdit(t)}
          className="rounded-full inline-flex items-center gap-1"
        >
          <PenSquare size={14} /> Edit
        </Button>
        {!t.is_default && (
          <Button
            size="sm"
            variant="outline"
            data-testid={`template-set-default-${t.id}`}
            onClick={() => onMakeDefault(t)}
            className="rounded-full inline-flex items-center gap-1"
          >
            <Star size={14} /> Set as default
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          data-testid={`template-delete-${t.id}`}
          onClick={() => onDelete(t)}
          className="rounded-full inline-flex items-center gap-1 ml-auto"
          style={{ color: "var(--terracotta)", borderColor: "rgba(31,93,52,0.35)" }}
        >
          <Trash2 size={14} /> Delete
        </Button>
      </div>
    </div>
  );
}

export default function VendorTemplates() {
  const { user, loading } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loadingTpls, setLoadingTpls] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoadingTpls(true);
    try {
      const { data } = await api.get("/vendor/application-templates");
      setTemplates(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load templates");
    } finally {
      setLoadingTpls(false);
    }
  }, []);

  useEffect(() => {
    if (user && user.role === "vendor") load();
  }, [user, load]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "vendor") return <Navigate to="/" replace />;

  const onDelete = async (t) => {
    if (!window.confirm(`Delete the "${t.name}" template? This can't be undone.`)) return;
    try {
      await api.delete(`/vendor/application-templates/${t.id}`);
      toast.success("Template deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete");
    }
  };

  const onMakeDefault = async (t) => {
    try {
      await api.patch(`/vendor/application-templates/${t.id}`, { is_default: true });
      toast.success(`"${t.name}" is now your default`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update");
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-14" data-testid="vendor-templates-page">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="overline" style={{ color: "var(--terracotta)" }}>Vendor · Application templates</div>
          <h1 className="font-serif text-5xl mt-3">Save a pitch. Apply in seconds.</h1>
          <p className="mt-4 text-sm max-w-2xl" style={{ color: "var(--ink-soft)" }}>
            Instead of retyping your pitch every time you spot a new event, save a few templates here.
            When you tap <em>Apply</em> on any event, you&apos;ll get a picker at the top of the form —
            one tap fills in your pitch, products, and booth size. Your default template is pre-selected.
          </p>
        </div>
        <Dialog open={creating} onOpenChange={setCreating}>
          <DialogTrigger asChild>
            <Button data-testid="new-template-button" className="btn-primary rounded-full inline-flex items-center gap-2">
              <PlusCircle size={16} /> New template
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle className="font-serif text-3xl">New template</DialogTitle></DialogHeader>
            <TemplateEditor
              mode="create"
              onSaved={() => load()}
              onClose={() => setCreating(false)}
            />
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-10">
        {loadingTpls ? (
          <div className="py-16 text-center font-serif text-2xl">Loading…</div>
        ) : templates.length === 0 ? (
          <div className="card-warm rounded-sm p-10 text-center" data-testid="templates-empty">
            <FileText size={28} className="mx-auto mb-3" style={{ color: "var(--terracotta)" }} />
            <div className="font-serif text-3xl">No templates yet.</div>
            <p className="mt-3 text-sm max-w-md mx-auto" style={{ color: "var(--ink-soft)" }}>
              Save your first pitch template above. You can save up to 10 — think one per event type
              (farmers market, holiday fair, food truck rally, etc.).
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="templates-grid">
            {templates.map((t) => (
              <TemplateCard
                key={t.id}
                t={t}
                onEdit={setEditing}
                onDelete={onDelete}
                onMakeDefault={onMakeDefault}
              />
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle className="font-serif text-3xl">Edit template</DialogTitle></DialogHeader>
          {editing && (
            <TemplateEditor
              mode="edit"
              initial={editing}
              onSaved={() => load()}
              onClose={() => setEditing(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      <div className="mt-10 text-sm">
        <Link to="/vendor/dashboard" className="underline" style={{ color: "var(--ink-soft)" }}>
          ← Back to dashboard
        </Link>
      </div>
    </div>
  );
}
